import { useState } from 'react';
import { Alert, Button, Snackbar, Stack } from '@mui/material';
import { sendTestToSelf, type TestSendRequest } from '../services/testSend';
import { useAuth } from '../context/AuthContext';

interface TestSendButtonProps extends TestSendRequest {
  disabled?: boolean;
}

export default function TestSendButton({ disabled, ...request }: TestSendButtonProps) {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fallbackNotice, setFallbackNotice] = useState<string | null>(null);

  async function handleClick() {
    setSending(true);
    setError(null);
    setFallbackNotice(null);
    try {
      const response = await sendTestToSelf(request);
      setResult(`Test sent to ${response.to}`);
      if (!response.sentAsSelf) {
        setFallbackNotice(
          'Sent from the default address with yours in Reply-To — your domain is not verified in Postmark, so a real campaign would go out the same way.',
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the test');
    } finally {
      setSending(false);
    }
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Button variant="outlined" disabled={disabled || sending} onClick={() => void handleClick()}>
          {sending ? 'Sending…' : 'Send a test to me'}
        </Button>
        {user?.email && (
          <span style={{ color: '#4a4a4a', fontSize: '0.875rem' }}>Goes to {user.email}</span>
        )}
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {fallbackNotice && <Alert severity="info">{fallbackNotice}</Alert>}

      <Snackbar
        open={Boolean(result)}
        autoHideDuration={6000}
        onClose={() => setResult(null)}
        message={result ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Stack>
  );
}
